import { Component } from 'preact';
import { TabBar } from './ui/TabBar';
import { UI } from '../utils/arena/uiSounds';
import {
  GOLD, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  DIALOG_STYLE, BEVELED_BTN,
  FourCorners,
} from '../lib/medievalTheme';
import { CloseButton } from './ui/navButtons';
import AdminPlayersTab from './admin/AdminPlayersTab';
import AdminEconomyTab from './admin/AdminEconomyTab';
import AdminBroadcastTab from './admin/AdminBroadcastTab';
import AdminMaintenanceTab from './admin/AdminMaintenanceTab';
import AdminSettingsTab from './admin/AdminSettingsTab';
import { hasCap, staffPanelTitle, onRolePreviewChange } from '../utils/staffAccess';

// A tab is offered when the account holds any capability it can act
// with. Moderators see only Players. Enforcement and investigation,
// no economy at all. QA testers see Economy (per-player grants +
// progression, with the economy-wide sections hidden inside the tab).
// Only the owner sees Broadcast, Maintenance and Settings.
const TAB_CAPS = {
  Players:     ['player.lookup', 'player.audit', 'player.ban', 'player.rename', 'match.logs', 'chat.moderate'],
  Economy:     ['grant.currency', 'grant.cards', 'progression.setLevel', 'economy.snapshot', 'grant.bulkAll'],
  Broadcast:   ['mail.test', 'mail.broadcast'],
  Maintenance: ['maintenance.manage'],
  Settings:    ['rc.allowlist', 'staff.manage', 'dev.achievements'],
};

export function visibleTabs(profile) {
  return Object.keys(TAB_CAPS).filter((tab) => TAB_CAPS[tab].some((cap) => hasCap(profile, cap)));
}

export default class AdminPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: visibleTabs(props.profile)[0] || null,
      log: [],
    };
  }

  // The role preview is module state, not a prop, so nothing re-renders
  // the panel when it flips. Without this, switching preview while the
  // panel is open leaves activeTab pointing at a tab the previewed role
  // doesn't hold. The tab bar updates but the pane below renders a tab
  // whose every section is gated off, i.e. blank.
  componentDidMount() {
    this._unsubscribePreview = onRolePreviewChange(() => this.forceUpdate());
  }

  componentWillUnmount() {
    this._unsubscribePreview?.();
  }

  componentDidUpdate() {
    const tabs = visibleTabs(this.props.profile);
    if (this.state.activeTab && !tabs.includes(this.state.activeTab)) {
      this.setState({ activeTab: tabs[0] || null });
    }
  }

  addLog = (message) => {
    this.setState((s) => ({ log: [...s.log.slice(-19), { time: new Date().toLocaleTimeString(), message }] }));
  };

  renderTabContent() {
    const { activeTab } = this.state;
    const { profile, sorceryCards, onUpdateProfile } = this.props;
    const tabProps = { profile, sorceryCards, onUpdateProfile, addLog: this.addLog };

    switch (activeTab) {
      case 'Players':   return <AdminPlayersTab {...tabProps} />;
      case 'Economy':   return <AdminEconomyTab {...tabProps} />;
      case 'Broadcast': return <AdminBroadcastTab {...tabProps} />;
      case 'Maintenance':  return <AdminMaintenanceTab {...tabProps} />;
      case 'Settings':     return <AdminSettingsTab {...tabProps} />;
      default:          return null;
    }
  }

  render() {
    const { onClose, profile } = this.props;
    const { activeTab, log } = this.state;
    const tabs = visibleTabs(profile);

    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }} onClick={onClose}>
        <div
          className="relative w-full max-w-[42cqw] h-[85cqh] flex flex-col overflow-hidden"
          style={{ ...DIALOG_STYLE }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners />

          {/* Header */}
          <div className="px-5 pt-4 pb-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${GOLD} 0.15)` }}>
            <h2 className="text-lg font-bold arena-heading" style={{ color: ACCENT_GOLD, textShadow: `0 0 12px ${GOLD} 0.2)` }}>
              {staffPanelTitle(profile)}
            </h2>
            <CloseButton onClick={onClose} />
          </div>

          {/* Tab bar */}
          <TabBar
            className="px-5 pt-3 pb-0 flex gap-1"
            style={{ borderBottom: `1px solid ${GOLD} 0.1)` }}
            variant="underline"
            barInset="0"
            barStyle={{ height: '2px', background: ACCENT_GOLD, boxShadow: 'none' }}
            activeIndex={tabs.indexOf(activeTab)}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  data-tab-item
                  className="px-4 py-2 text-[0.6875cqw] font-semibold"
                  style={{
                    color: isActive ? ACCENT_GOLD : TEXT_MUTED,
                    background: 'transparent',
                    marginBottom: '-1px',
                  }}
                  onClick={() => this.setState({ activeTab: tab })}
                >
                  {tab}
                </button>
              );
            })}
          </TabBar>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
            {this.renderTabContent()}

            {/* Log (always visible at bottom) */}
            <Section title="Log">
              <div className="max-h-28 overflow-y-auto flex flex-col gap-0.5">
                {log.length === 0 ? (
                  <span className="text-[0.6875cqw]" style={{ color: TEXT_MUTED }}>No actions yet</span>
                ) : log.map((entry, i) => (
                  <div key={i} className="text-[0.6875cqw]" style={{ color: TEXT_BODY }}>
                    <span style={{ color: TEXT_MUTED }}>[{entry.time}]</span> {entry.message}
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </div>
    );
  }
}

export function Section({ title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[0.625cqw] font-semibold arena-heading" style={{ color: `${GOLD} 0.5)` }}>{title}</span>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${GOLD} 0.2), transparent)` }} />
      </div>
      {children}
    </div>
  );
}

export function AdminButton({ onClick, disabled, children }) {
  return (
    <button
      type="button"
      className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03] active:scale-[0.97] disabled:opacity-40"
      style={BEVELED_BTN}
      onClick={onClick}
      disabled={disabled}
    >
      <span style={{ color: TEXT_BODY }}>{children}</span>
    </button>
  );
}
